/**
 * Data-fetching helpers for the Professional Details card (DF169-177). Keeps the
 * view/editor components free of API-shape knowledge.
 */
import * as api from "../../../../../Utils/apis/professionalContactsApi";
import { LOAN_DETAIL_ROLE_KEYS } from "../../../../../domain/professionalContacts/professionalContacts.types";

/** An empty PartyAssignment for a role that hasn't been filled in yet. */
export const emptyAssignment = () => ({ firmId: null, contactId: null, addressId: null, dates: {} });

/**
 * Normalises whatever the API returns into a shape every role key is guaranteed to have —
 * so the read view/editor never has to null-check `details.bankSolicitor` etc.
 * @param {string} loanId
 * @param {import("../../../../../domain/professionalContacts/professionalContacts.types").LoanProfessionalDetails} [raw]
 */
export function normaliseDetails(loanId, raw) {
  const details = { loanId, quotes: raw?.quotes || [], notes: raw?.notes ?? null };
  Object.values(LOAN_DETAIL_ROLE_KEYS).forEach((key) => {
    details[key] = { ...emptyAssignment(), ...(raw?.[key] || {}) };
  });
  return details;
}

/**
 * The read view needs each assigned firm's contact/address resolved (number, email, full
 * address) from just the firmId/contactId/addressId a PartyAssignment carries — fetch every
 * distinct firm referenced by a role assignment or a quote, once each.
 * @param {import("../../../../../domain/professionalContacts/professionalContacts.types").LoanProfessionalDetails} details
 * @returns {Promise<Object.<string, import("../../../../../domain/professionalContacts/professionalContacts.types").Firm>>}
 */
export async function fetchAssignedFirms(details) {
  const firmIds = new Set();
  Object.values(LOAN_DETAIL_ROLE_KEYS).forEach((key) => {
    if (details[key]?.firmId) firmIds.add(details[key].firmId);
  });
  (details.quotes || []).forEach((quote) => quote.firmId && firmIds.add(quote.firmId));

  const firms = await Promise.all(
    Array.from(firmIds).map((firmId) => api.getFirm(firmId).catch(() => null))
  );
  const firmsById = {};
  Array.from(firmIds).forEach((firmId, index) => {
    if (firms[index]) firmsById[firmId] = firms[index];
  });
  return firmsById;
}

export { api };
